import { AtendimentoTicket } from '../services/atendimentoService';

export interface AtendimentoVisibilidadeOptions {
  userId: number | null;
  podeVerTicketsSemSetor: boolean;
  podeVerTicketsOutrosUsuarios: boolean;
  podeExibirTicketsBot: boolean;
}

/**
 * Utilitarios compartilhados do modulo de atendimento.
 *
 * Secoes adicionadas para manutencao:
 * - Assuncao de ticket por outro atendente (fechar chat de quem perdeu a corrida)
 * - reaction/sticker em ticket fechado (nao reabrir fila nem notificar como nova conversa)
 */

export const ticketAssumidoPorOutroUsuario = (
  ticket: AtendimentoTicket,
  userId: number | null | undefined,
): boolean => {
  if (ticket.status !== 'em_atendimento' || !ticket.usuario_id || !userId) return false;
  return Number(ticket.usuario_id) !== Number(userId);
};

/** Ticket passou de aguardando para atendendo com outro usuario (evento socket/API). */
export const ticketFoiAssumidoPorOutroEnquantoAguardava = (
  ticket: AtendimentoTicket,
  ticketAnterior: AtendimentoTicket | null | undefined,
  userId: number | null | undefined,
): boolean => {
  if (!ticketAnterior || ticketAnterior.status !== 'pendente') return false;
  if (ticket.status !== 'em_atendimento') return false;
  return ticketAssumidoPorOutroUsuario(ticket, userId);
};

export const isAtendimentoTicketFechado = (ticket: Pick<AtendimentoTicket, 'status'>) => (
  ticket.status === 'fechado' || ticket.status === 'fechado_bot'
);

/** reaction e sticker apos fechamento nao devem reabrir ticket na fila ativa. */
export const mensagemNaoReabreTicketFechado = (mensagemTipo?: string | null) => (
  mensagemTipo === 'reaction' || mensagemTipo === 'sticker'
);

/** Evita incluir/atualizar ticket fechado na lista ativa (exceto se ja estiver aberto/visivel). */
export const deveIgnorarAtualizacaoTicketAtivo = (
  ticket: AtendimentoTicket,
  options?: {
    mensagemTipo?: string | null;
    ticketJaNaLista?: boolean;
    ticketAberto?: boolean;
  },
) => {
  if (!isAtendimentoTicketFechado(ticket)) return false;
  if (options?.ticketAberto) return false;
  if (options?.ticketJaNaLista) return false;
  if (mensagemNaoReabreTicketFechado(options?.mensagemTipo)) return true;
  return !options?.ticketJaNaLista;
};

export const isAtendimentoTicketVisivel = (
  ticket: AtendimentoTicket,
  options: AtendimentoVisibilidadeOptions
): boolean => {
  if (!options.podeVerTicketsSemSetor && !ticket.setor_id) return false;

  const ticketUsuarioId = ticket.usuario_id ? Number(ticket.usuario_id) : null;
  if (
    !options.podeVerTicketsOutrosUsuarios
    && options.userId
    && ticketUsuarioId
    && ticketUsuarioId !== options.userId
  ) {
    return false;
  }

  if (!options.podeExibirTicketsBot && ticket.is_bot && ticket.status !== 'fechado') {
    return false;
  }

  return true;
};

export const deveTocarSomParaAtendimentoTicket = (
  ticket: AtendimentoTicket,
  options: AtendimentoVisibilidadeOptions,
  openTicketId: number | null
): boolean => {
  if (openTicketId === ticket.id) return false;
  if (!isAtendimentoTicketVisivel(ticket, options)) return false;
  
  // Não toca som para tickets originados de disparo automático/outbound (onde não há mensagens do contato para ler)
  if (!ticket.msgs_nao_lidas || ticket.msgs_nao_lidas === 0) return false;

  const ticketUsuarioId = ticket.usuario_id ? Number(ticket.usuario_id) : null;
  return !ticketUsuarioId || (options.userId !== null && ticketUsuarioId === options.userId);
};

export const calcularBadgeAtendimento = (
  tickets: AtendimentoTicket[],
  options: AtendimentoVisibilidadeOptions,
  openTicketId: number | null
) => {
  let totalNaoLidas = 0;
  let ticketsPendentes = 0;

  for (const ticket of tickets) {
    if (!isAtendimentoTicketVisivel(ticket, options)) continue;
    if (['fechado', 'fechado_bot'].includes(ticket.status)) continue;

    if (ticket.status === 'pendente' && !ticket.is_bot) {
      ticketsPendentes += 1;
    }

    if (ticket.id === openTicketId) continue;

    totalNaoLidas += ticket.msgs_nao_lidas || 0;
  }

  const badgeCount = totalNaoLidas > 0 ? totalNaoLidas : ticketsPendentes;

  return {
    totalNaoLidas,
    ticketsPendentes,
    badgeCount,
    temNovidade: totalNaoLidas > 0 || ticketsPendentes > 0,
  };
};
